<template>
  <div class="hero-search-result" v-if="heroArray">
    <div
      class="hero-search-result__empty-text card"
      v-if="heroArray.length === 0"
    >
      No users matching your query
    </div>

    <div v-if="heroSelectableArray.length > 0">
      <div class="row">
        <div class="col col-xs-12 col-md-4">
          <div class="hero-search-result__list card">
            <div class="hero-search-result__list-header">
              <div class="hero-search-result__list-title">
                Search results
              </div>

              <el-button type="primary" @click="toggleSelectAll">
                {{ isAllSelected ? 'Reset select' : 'Select all' }}
              </el-button>
            </div>

            <div
              :class="[
                'hero-search-result__hero',
                { 'hero-search-result__hero--active': isUserActive(hero) },
              ]"
              v-for="hero in heroSelectableArray"
              :key="hero.userId"
            >
              <div
                class="hero-search-result__hero-info"
                @click="setActiveUser(hero)"
              >
                <el-avatar class="hero-search-result__hero-avatar" :size="32">
                  <user-avatar :userId="hero.userId" :alt="hero.name" />
                </el-avatar>

                <span class="hero-search-result__hero-name">
                  {{ hero.name }}
                </span>
              </div>

              <span class="hero-search-result__hero-check">
                <el-checkbox v-model="hero.isSelected"></el-checkbox>
              </span>
            </div>
          </div>

          <el-button
            class="hero-search-result__button"
            type="primary"
            @click="handleInviteHeroes"
            :disabled="!isHeroesSelected"
          >
            Invite
          </el-button>
        </div>

        <div class="col col-xs-12 col-md-8">
          <div class="hero-search-result__preview" v-if="activeUser">
            <public-c-v-preview :user-id="activeUser.userId" />
          </div>

          <div class="hero-search-result__preview-empty card" v-else>
            Click on a user from search results to view their CV
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="isInviteFormVisible"
      width="40%"
      title="Invite Heroes"
    >
      <p>
        Do you want to send an invitation to these people:
      </p>

      <div class="hero-search-result__user-list">
        <div class="row">
          <div
            class="col col-xs-12 col-md-6"
            v-for="hero in selectedHeroes"
            :key="hero.userId"
          >
            <div class="hero-search-result__user-preview">
              <el-avatar
                class="hero-search-result__user-preview-avatar"
                :size="40"
              >
              <user-avatar :userId="hero.userId" :alt="hero.name" />
              </el-avatar>
              <span class="hero-search-result__user-preview-name">
                {{ hero.name }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <el-form
        :rules="inviteUsersRules"
        :model="inviteUsersForm"
        ref="inviteUsersForm"
      >
        <el-form-item
          prop="expirationDate"
          :error="inviteUsersErrors.expirationDate"
          label="Expiration date"
        >
          <el-date-picker
            class="wide"
            v-model="inviteUsersForm.expirationDate"
            type="datetime"
            format="DD/MM/YYYY"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item
          prop="comments"
          :error="inviteUsersErrors.comments"
          label="Comment"
        >
          <el-input
            v-model="inviteUsersForm.comments"
            type="textarea"
            :rows="4"
            placeholder="Enter your comment text"
          >
          </el-input>
        </el-form-item>

        <el-form-item prop="preApproved" :error="inviteUsersErrors.preApproved">
          <el-checkbox v-model="inviteUsersForm.preApproved">
            Pre-approve applications of these users
          </el-checkbox>
        </el-form-item>
      </el-form>

      <template v-slot:footer>
        <el-button @click="isInviteFormVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="validateInviteForm"
          :disabled="isButtonDisabled('invite-users')"
        >
          Invite
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script>
import PublicCVPreview from '@/components/profile/PublicCVPreview.vue';
import { inviteUsersToEvent, getEvent } from '@/api/events.js';
import { showMessage } from '@/utils/messages.js';
import { parseErrorsByFields } from '@/utils/index.js';
import { disableButton } from '@/mixins/disableButton.js';
import UserAvatar from '@/components/profile/UserAvatar.vue';

export default {
  name: 'SearchResult',
  props: {
    total: {
      type: Number,
      required: false,
    },
    heroArray: {
      type: Array,
      required: false,
    },
  },
  mixins: [disableButton],
  components: {
    PublicCVPreview,
    UserAvatar,
  },
  data() {
    return {
      heroSelectableArray: [],
      activeUser: null,
      isInviteFormVisible: false,
      inviteUsersRules: {
        comments: [
          {
            required: false,
            trigger: 'input',
            message: '',
          },
        ],
        preApproved: [
          {
            required: false,
            trigger: 'change',
            message: '',
          },
        ],
        expirationDate: [
          {
            required: true,
            trigger: 'change',
            message: 'Please select expiration date of invitation',
          },
        ],
      },
      inviteUsersForm: {
        comments: null,
        expirationDate: null,
        preApproved: false,
      },
      inviteUsersErrors: {
        comments: null,
        expirationDate: null,
        preApproved: null,
      },
      eventData: null,
    };
  },
  computed: {
    isAllSelected() {
      return this.heroSelectableArray.every((hero) => hero.isSelected);
    },
    isHeroesSelected() {
      return this.heroSelectableArray.some((hero) => hero.isSelected);
    },
    selectedHeroes() {
      return this.heroSelectableArray.filter((hero) => hero.isSelected);
    },
    eventId() {
      return Number(this.$route.query.eventId) || null;
    },
    inviteFormData() {
      const { eventId, selectedHeroes } = this;
      const { comments, expirationDate, preApproved } = this.inviteUsersForm;
      const userIds = selectedHeroes.map((hero) => hero.userId);

      return {
        eventId,
        userIds,
        preApproved,
        comments,
        expirationDate,
      };
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    async requestInviteUsers() {
      this.disableButton('invite-users');
      const { inviteFormData } = this;
      const { success, errors } = await inviteUsersToEvent(inviteFormData);
      this.enableButton('invite-users');

      if (success) {
        this.isInviteFormVisible = false;

        this.showMessage({
          type: 'success',
          message: 'Invitations have been sent',
        });
        this.clearInviteForm();
        this.$emit('update-data');
      } else if (errors) {
        this.inviteUsersErrors = {
          ...this.inviteUsersErrors,
          ...this.parseErrorsByFields(errors),
        };
      }
    },
    async fetchEventData() {
      const { eventId } = this;
      const { data } = await getEvent({ eventId });

      this.eventData = data;
    },
    setData(heroData) {
      this.heroSelectableArray = heroData.map((hero) => {
        return {
          userId: hero.userContactInfo.userId,
          name:
            hero.userContactInfo.firstName +
            (hero.userContactInfo.lastName === null
              ? ''
              : ' ' + hero.userContactInfo.lastName),
          isSelected: false,
        };
      });
    },
    toggleSelectAll() {
      const { isAllSelected } = this;

      this.heroSelectableArray.forEach((hero) => {
        hero.isSelected = !isAllSelected;
      });
    },
    setActiveUser(user) {
      this.activeUser = user;
    },
    isUserActive(user) {
      const { activeUser } = this;
      if (activeUser) {
        return activeUser.userId === user.userId;
      }
      return false;
    },
    handleInviteHeroes() {
      this.isInviteFormVisible = true;

      const { expirationDate } = this.inviteUsersForm;
      const { startDate } = this.eventData;
      
      if (startDate) {
        this.inviteUsersForm.expirationDate = startDate;
      }
    },
    validateInviteForm() {
      const { expirationDate } = this.inviteUsersForm;

      if (this.$refs.inviteUsersForm) {
        this.$refs.inviteUsersForm.validate();
      }
      if (expirationDate) {
        this.requestInviteUsers();
      }
    },
    clearInviteForm() {
      this.inviteUsersForm.comments = null;
      this.inviteUsersForm.expirationDate = null;
    },
  },
  created() {
    const { heroArray } = this;
    if (heroArray) {
      this.setData(heroArray);
    }

    this.fetchEventData();
  },
  watch: {
    heroArray(newVal) {
      if (newVal) {
        this.setData(newVal);
      }
    },
  },
};
</script>

<style lang="scss">
.hero-search-result {
  $this: &;

  &__empty-text {
  }

  &__list {
  }

  &__list-header {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--global-dimension-size-225);
  }

  &__list-title {
  }

  &__hero {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    padding: var(--global-dimension-size-125) 0;

    &--active {
      #{$this}__hero-info {
        color: var(--color-primary-400);
      }
    }
  }

  &__hero-info {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    cursor: pointer;

    &:hover {
      color: var(--color-primary-400);
    }
  }

  &__hero-avatar {
    margin-right: var(--global-dimension-size-130);
  }

  &__hero-name {
    flex-grow: 1;
    font-size: var(--font-size-200);
  }

  &__hero-check {
  }

  &__preview {
  }

  &__preview-empty {
  }

  &__button {
    display: block;
    margin-left: auto;
    margin-top: var(--global-dimension-size-250);
  }

  &__user-list {
    margin-bottom: var(--global-dimension-size-175);
  }

  &__user-preview {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    margin-bottom: var(--global-dimension-size-200);
  }

  &__user-preview-avatar {
    margin-right: var(--global-dimension-size-125);
  }

  &__user-preview-name {
    font-size: var(--font-size-200);
  }
}
</style>
